import React, { useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import RelicSprite from '../shared/RelicSprite';
import { LockIcon } from '../shared/RelicVaultIcons';
import { gridItemWidth } from '../shared/useRelicVaultLayout';
import { COLORS, bodyFont, em, headingFont, hoverTransition } from '../shared/theme';
import { iconGlowFor } from 'utils/relicVault/colorUtils';
import { rarityColorFor, undiscoveredName } from 'utils/relicVault/relicDisplay';
import { IS_WEB, webOnly } from 'utils/relicVault/webStyle';

/** Tier-specific treatment for undiscovered cards (.vault-card.locked-<key>). */
const LOCKED_ACCENT = {
  legendary: {
    border: 'rgba(251,191,36,0.45)',
    shadow: '0 0 16px rgba(251,191,36,0.2), inset 0 0 14px rgba(251,191,36,0.06)',
    background: 'linear-gradient(180deg, rgba(251,191,36,0.07), rgba(0,0,0,0.18))',
    iconGlow: 'radial-gradient(circle, rgba(251,191,36,0.2), transparent 70%)',
    iconGlowNative: 'rgba(251,191,36,0.14)',
    name: '#E8C77A',
  },
  mythic: {
    border: 'rgba(244,114,182,0.4)',
    shadow: '0 0 16px rgba(244,114,182,0.18)',
    iconGlow: 'radial-gradient(circle, rgba(244,114,182,0.2), transparent 70%)',
    iconGlowNative: 'rgba(244,114,182,0.14)',
    name: '#E8A6C7',
  },
};

/** One rarity tier of the selected guild: header + grid, or the mythic feature row. */
const RaritySection = ({ section, selectedRelicId, columns, gap, isPhone, onSelect }) => {
  const [gridWidth, setGridWidth] = useState(0);

  return (
    <View style={styles.section}>
      <View style={styles.head}>
        <View style={[styles.dot, { backgroundColor: section.color }]} />
        <Text style={[styles.title, { color: section.color }]}>{section.label}</Text>
        <Text style={styles.count}>
          {section.owned} / {section.total}
        </Text>
      </View>

      {section.isMythicFeature ? (
        <View style={[styles.mythicRow, isPhone && styles.mythicRowPhone]}>
          {section.items.map(relic => (
            <MythicFeatureCard key={relic.id} relic={relic} onSelect={onSelect} />
          ))}
        </View>
      ) : (
        <View
          style={[styles.grid, { marginBottom: -gap }]}
          onLayout={e => setGridWidth(e.nativeEvent.layout.width)}>
          {gridWidth > 0 &&
            section.items.map((relic, index) => (
              <View
                key={relic.id}
                style={{
                  marginRight: (index + 1) % columns === 0 ? 0 : gap,
                  marginBottom: gap,
                }}>
                <VaultCard
                  relic={relic}
                  selected={selectedRelicId === relic.id}
                  width={gridItemWidth(gridWidth, columns, gap)}
                  onSelect={onSelect}
                />
              </View>
            ))}
        </View>
      )}
    </View>
  );
};

const VaultCard = React.memo(({ relic, selected, width, onSelect }) => {
  const [hovered, setHovered] = useState(false);
  const locked = !relic.owned;
  const accent = locked ? LOCKED_ACCENT[relic.rarity] : null;
  const glowColor = rarityColorFor(relic);

  let iconBackground;
  if (!locked) {
    iconBackground = IS_WEB ? { backgroundImage: iconGlowFor(glowColor) } : { backgroundColor: `${glowColor}22` };
  } else if (accent) {
    iconBackground = IS_WEB ? { backgroundImage: accent.iconGlow } : { backgroundColor: accent.iconGlowNative };
  } else {
    iconBackground = { backgroundColor: 'rgba(255,255,255,0.03)' };
  }

  return (
    <TouchableOpacity
      activeOpacity={0.9}
      onPress={() => onSelect(relic.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={locked ? undiscoveredName(relic) : relic.name}
      style={[
        styles.card,
        { width },
        accent && { borderColor: accent.border },
        accent && webOnly({ boxShadow: accent.shadow, backgroundImage: accent.background }),
        hovered && styles.cardHover,
        hovered && !accent && styles.cardHoverBorder,
        selected && styles.cardSelected,
        selected && webOnly({ boxShadow: '0 0 0 1.5px rgba(255,255,255,0.2)' }),
        hoverTransition(120),
      ]}>
      <View style={styles.iconWrap}>
        {/* ::before - the icon glow, blurred outward past the wrap */}
        <View
          pointerEvents="none"
          style={[styles.iconGlow, iconBackground, webOnly({ filter: 'blur(11px)' })]}
        />
        <RelicSprite relic={relic} size={80} locked={locked} shadow="rgba(0,0,0,0.45)" />
        {locked && (
          <View style={styles.lockOverlay}>
            <LockIcon size={9} />
          </View>
        )}
      </View>
      <Text
        numberOfLines={2}
        style={[
          styles.name,
          locked && styles.nameLocked,
          accent && { color: accent.name },
        ]}>
        {locked ? undiscoveredName(relic) : relic.name}
      </Text>
    </TouchableOpacity>
  );
});

const MythicFeatureCard = ({ relic, onSelect }) => {
  const [hovered, setHovered] = useState(false);
  const owned = relic.owned;

  return (
    <TouchableOpacity
      activeOpacity={0.9}
      onPress={() => onSelect(relic.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={owned ? relic.name : 'Undiscovered Mythic Relic'}
      style={[
        styles.mythicCard,
        owned && styles.mythicCardOwned,
        webOnly({
          backgroundImage:
            'radial-gradient(ellipse 640px 260px at 50% -10%, rgba(244,114,182,0.22), transparent 60%), linear-gradient(180deg, rgba(40,10,30,0.65), rgba(10,6,20,0.85))',
          boxShadow: owned
            ? '0 0 60px rgba(244,114,182,0.3), inset 0 0 40px rgba(244,114,182,0.1)'
            : '0 0 44px rgba(244,114,182,0.14), inset 0 0 40px rgba(244,114,182,0.05)',
        }),
        hovered && styles.mythicCardHover,
        hoverTransition(120),
      ]}>
      {owned ? (
        <>
          <View style={styles.mythicIconWrap}>
            <View
              pointerEvents="none"
              style={[
                styles.mythicIconGlow,
                IS_WEB
                  ? { backgroundImage: iconGlowFor(rarityColorFor(relic)), filter: 'blur(14px)' }
                  : { backgroundColor: 'rgba(244,114,182,0.2)' },
              ]}
            />
            <RelicSprite relic={relic} size={88} shadow="rgba(0,0,0,0.45)" />
          </View>
          <Text style={styles.mythicOwnedName}>{relic.name}</Text>
          <Text style={styles.mythicOwnedDesc}>{relic.desc}</Text>
          <View style={styles.mythicOwnedBadge}>
            <Text style={styles.mythicOwnedBadgeText}>In Your Collection</Text>
          </View>
        </>
      ) : (
        <>
          <View
            style={[
              styles.mythicLock,
              IS_WEB
                ? { backgroundImage: 'radial-gradient(circle, rgba(244,114,182,0.3), transparent 72%)' }
                : { backgroundColor: 'rgba(244,114,182,0.18)' },
            ]}>
            <LockIcon size={34} color="#F472B6" strokeWidth="2" />
          </View>
          <Text style={styles.mythicLabel}>MYTHIC RELIC</Text>
          <Text style={styles.mythicMystery}>??????????</Text>
          <Text style={styles.mythicSub}>A legendary secret awaits{'…'}</Text>
        </>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  section: {
    marginTop: 20,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: 9,
    borderBottomWidth: 1.5,
    borderBottomColor: 'rgba(255,255,255,0.08)',
    marginBottom: 13,
  },
  dot: {
    width: 9,
    height: 9,
    borderRadius: 4.5,
    marginRight: 9,
  },
  title: {
    ...headingFont('800'),
    fontSize: 13.5,
    letterSpacing: em(0.09, 13.5),
    textTransform: 'uppercase',
  },
  count: {
    ...bodyFont('700'),
    fontSize: 12,
    color: COLORS.muted,
    marginLeft: 'auto',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  card: {
    borderRadius: 14,
    paddingTop: 14,
    paddingHorizontal: 8,
    paddingBottom: 12,
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  cardHover: {
    transform: [{ translateY: -2 }],
  },
  cardHoverBorder: {
    borderColor: 'rgba(255,255,255,0.20)',
  },
  cardSelected: {
    borderColor: 'rgba(255,255,255,0.45)',
  },
  iconWrap: {
    width: 96,
    height: 96,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconGlow: {
    position: 'absolute',
    top: -10,
    left: -10,
    right: -10,
    bottom: -10,
    borderRadius: 999,
    opacity: 0.85,
  },
  lockOverlay: {
    position: 'absolute',
    top: 39,
    left: 39,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: 'rgba(6,5,18,0.9)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  name: {
    ...headingFont('700'),
    fontSize: 12.5,
    lineHeight: 16.25,
    letterSpacing: em(0.005, 12.5),
    minHeight: 30,
    marginTop: 8,
    color: COLORS.textBright,
    textAlign: 'center',
  },
  nameLocked: {
    color: COLORS.muted,
    fontStyle: 'italic',
  },
  mythicRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginRight: -16,
    marginBottom: -16,
  },
  mythicRowPhone: {
    justifyContent: 'center',
  },
  mythicCard: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 240,
    marginRight: 16,
    marginBottom: 16,
    borderRadius: 22,
    paddingVertical: 32,
    paddingHorizontal: 24,
    alignItems: 'center',
    backgroundColor: 'rgba(25,8,25,0.75)',
    borderWidth: 1.5,
    borderColor: 'rgba(244,114,182,0.4)',
  },
  mythicCardOwned: {
    borderColor: 'rgba(244,114,182,0.6)',
  },
  mythicCardHover: {
    transform: [{ translateY: -2 }],
    borderColor: 'rgba(244,114,182,0.75)',
  },
  mythicLock: {
    width: 88,
    height: 88,
    borderRadius: 44,
    marginBottom: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: 'rgba(244,114,182,0.5)',
  },
  mythicLabel: {
    ...headingFont('900'),
    fontSize: 12.5,
    letterSpacing: em(0.16, 12.5),
    color: '#F472B6',
    marginBottom: 8,
  },
  mythicMystery: {
    ...headingFont('800'),
    fontSize: 22,
    letterSpacing: em(0.32, 22),
    color: COLORS.text,
    marginBottom: 10,
    marginLeft: 6,
  },
  mythicSub: {
    ...bodyFont('400'),
    fontSize: 13,
    fontStyle: 'italic',
    color: COLORS.lilacSoft,
    textAlign: 'center',
  },
  mythicIconWrap: {
    width: 100,
    height: 100,
    marginBottom: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mythicIconGlow: {
    position: 'absolute',
    top: -12,
    left: -12,
    right: -12,
    bottom: -12,
    borderRadius: 999,
    opacity: 0.85,
  },
  mythicOwnedName: {
    ...headingFont('800'),
    fontSize: 22,
    color: COLORS.textWarm,
    marginBottom: 6,
    textAlign: 'center',
  },
  mythicOwnedDesc: {
    ...bodyFont('400'),
    fontSize: 13,
    lineHeight: 18.85,
    color: COLORS.lilacSoft,
    marginBottom: 14,
    textAlign: 'center',
  },
  mythicOwnedBadge: {
    borderWidth: 1.5,
    borderColor: 'rgba(244,114,182,0.5)',
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 12,
  },
  mythicOwnedBadgeText: {
    ...headingFont('800'),
    fontSize: 11,
    color: '#F472B6',
  },
});

export default RaritySection;
